/**
 * CaribbeanLeaf API Configuration
 *
 * MIRRORS: caribbeanleaf-api/docs/API_CONTRACT.md
 *
 * Centralized endpoint configuration for the cannabis dispensary POS.
 * All endpoints match the locked API contract from INT-02.
 */

// Base URL from environment, defaults to local dev
// Note: VITE_API_URL should include the full path e.g. http://localhost:5001/api/v1
export const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'http://localhost:5001/api/v1';

// API version prefix (for reference only, not used in URL construction)
export const API_VERSION = '/api/v1';

// Full API URL - use API_BASE_URL directly since it includes version
export const API_URL = API_BASE_URL;

/**
 * Request timeout in milliseconds
 * - Default: 30s for most operations
 * - Extended: 60s for report generation
 */
export const DEFAULT_TIMEOUT = 30_000;
export const EXTENDED_TIMEOUT = 60_000;

/**
 * API Endpoints
 *
 * Organized by domain module matching backend controllers:
 * - auth: Authentication and session management
 * - patients: Patient registry and limits
 * - inventory: Products and batches
 * - pos: Transactions and checkout
 * - compliance: Reports and validation
 */
export const ENDPOINTS = {
  // Health check (no auth required)
  health: '/health',

  // Authentication
  auth: {
    login: '/auth/login',
    refresh: '/auth/refresh',
    me: '/auth/me',
    logout: '/auth/logout',
  },

  // Patients
  patients: {
    list: '/patients',
    create: '/patients',
    detail: (id: number) => `/patients/${id}`,
    update: (id: number) => `/patients/${id}`,
    limits: (id: number) => `/patients/${id}/limits`,
    search: '/patients/search',
  },

  // Inventory
  inventory: {
    products: {
      list: '/inventory/products',
      create: '/inventory/products',
      detail: (id: number) => `/inventory/products/${id}`,
      update: (id: number) => `/inventory/products/${id}`,
      availability: (id: number) => `/inventory/products/${id}/availability`,
    },
    batches: {
      list: '/inventory/batches',
      create: '/inventory/batches',
      detail: (id: number) => `/inventory/batches/${id}`,
      adjust: (id: number) => `/inventory/batches/${id}/adjust`,
    },
  },

  // POS
  pos: {
    transactions: {
      list: '/pos/transactions',
      create: '/pos/checkout',
      detail: (id: number) => `/pos/transactions/${id}`,
      void: (id: number) => `/pos/transactions/${id}/void`,
    },
    dailyTotals: '/pos/daily-totals',
    patientLimit: (id: number) => `/pos/patients/${id}/daily-limit`,
  },

  // Compliance
  compliance: {
    reports: {
      list: '/compliance/reports',
      available: '/compliance/reports/available',
      generate: '/compliance/reports/generate',
      detail: (id: number) => `/compliance/reports/${id}`,
    },
    validate: '/compliance/validate',
    jurisdiction: '/compliance/jurisdiction',
  },
  // Staff Orders
  staffOrders: {
    pending: '/orders/staff/pending',
    active: '/orders/staff/active',
    detail: (id: number) => `/orders/staff/${id}`,
    updateStatus: (id: number) => `/orders/staff/${id}/status`,
    cancel: (id: number) => `/orders/staff/${id}/cancel`,
  },

  // Inventory deduction
  inventoryDeduct: '/inventory/deduct',

  // Patient verify-purchase
  patientVerifyPurchase: (id: number) => `/patients/${id}/verify-purchase`,

  // Notifications
  notifications: {
    subscribe: '/notifications/subscribe',
    unsubscribe: '/notifications/unsubscribe',
    devices: '/notifications/devices',
    send: '/notifications/send',
    topics: '/notifications/topics',
  },

  // Storage
  storage: {
    upload: '/storage/upload',
    uploadPublic: '/storage/upload/public',
    presign: '/storage/presign',
    delete: '/storage/delete',
    info: '/storage/info',
  },
} as const;

/**
 * Build full URL for an endpoint
 */
export function buildUrl(path: string): string {
  return `${API_URL}${path}`;
}

/**
 * Build URL with query parameters
 */
export function buildUrlWithParams(
  path: string,
  params?: Record<string, string | number | boolean | undefined>
): string {
  const url = new URL(buildUrl(path));

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.append(key, String(value));
      }
    });
  }

  return url.toString();
}
